import { lockedCareerMetric } from '../../utils/missionTaskActions';

export const COMPLETION_MODES = [
  { value: 'all', label: 'All — every requirement must complete' },
  { value: 'any_n_of_m', label: 'Any N of M — complete required_count from optional pool' },
  { value: 'streak_plus_any_n', label: 'Streak + Any N — required pool + required_count optional' },
];

export const METRIC_TYPES = [
  { value: 'count', label: 'Count — total actions' },
  { value: 'distinct_count', label: 'Distinct count — unique dedup keys' },
  { value: 'streak_days', label: 'Streak days — activity streak length' },
  { value: 'wins', label: 'Wins — Super Over wins' },
  { value: 'consecutive_wins', label: 'Consecutive wins — Super Over win streak' },
  { value: 'score_gte', label: 'Score ≥ — min score threshold' },
  { value: 'runs_gte', label: 'Runs ≥ — min runs threshold' },
  { value: 'level_gte', label: 'Level ≥ — min level reached' },
];

export const POOL_GROUPS = [
  { value: 'required', label: 'Required' },
  { value: 'optional', label: 'Optional' },
];

export const PARTICIPANT_STATUSES = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed (no active mission)' },
];

export const SUBJECT_TYPES = [
  { value: '', label: 'All types' },
  { value: 'user', label: 'User' },
  { value: 'guest', label: 'Guest' },
];

export function formatNumber(value) {
  if (value == null || Number.isNaN(Number(value))) return '0';
  return Number(value).toLocaleString();
}

export function completionModeLabel(mode) {
  return COMPLETION_MODES.find((m) => m.value === mode)?.label || mode;
}

export function completionModeShort(mode) {
  if (mode === 'all') return 'all';
  if (mode === 'any_n_of_m') return 'any N of M';
  if (mode === 'streak_plus_any_n') return 'streak + N';
  return mode || '—';
}

export function parseActionOptions(payload) {
  const raw = payload?.actions || payload?.action_keys || payload;
  if (!Array.isArray(raw)) return [];
  return raw.map((action) => {
    if (typeof action === 'string') {
      return { action_key: action, label: action };
    }
    const key = action.action_key || action.key || '';
    return {
      action_key: key,
      label: action.label || action.name || key,
    };
  }).filter((a) => a.action_key);
}

export function findChainTail(missions) {
  if (!Array.isArray(missions) || missions.length === 0) return null;
  const tails = missions.filter((m) => m.next_mission_id == null);
  const pool = tails.length ? tails : missions;
  return pool.slice().sort((a, b) => (b.sort_order ?? 0) - (a.sort_order ?? 0))[0] || null;
}

export function nextSortOrder(missions) {
  if (!Array.isArray(missions) || missions.length === 0) return 1;
  return Math.max(...missions.map((m) => Number(m.sort_order) || 0)) + 1;
}

export function missionChains(missions) {
  const list = Array.isArray(missions) ? missions : [];
  const byId = new Map(list.map((m) => [m.id, m]));
  const pointedTo = new Set(list.map((m) => m.next_mission_id).filter((id) => id != null));
  const heads = list
    .filter((m) => !pointedTo.has(m.id))
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  return heads.map((head) => {
    const chain = [];
    const seen = new Set();
    let current = head;
    while (current && !seen.has(current.id)) {
      seen.add(current.id);
      chain.push(current);
      current = current.next_mission_id != null ? byId.get(current.next_mission_id) : null;
    }
    return chain;
  });
}

export function emptyRequirement(sortOrder = 1) {
  return {
    action_key: '',
    target_value: 1,
    metric_type: 'count',
    distinct_dimension: '',
    pool_group: 'required',
    sort_order: sortOrder,
    label: '',
  };
}

export function emptyMissionForm() {
  return {
    stage: '',
    name: '',
    purpose: '',
    image_url: '',
    sort_order: 1,
    reward_fe: 100,
    next_mission_id: '',
    completion_mode: 'all',
    required_count: '',
    required_streak_days: '',
    is_active: true,
    requirements: [emptyRequirement(1)],
  };
}

export function missionToForm(mission) {
  if (!mission) return emptyMissionForm();
  return {
    stage: mission.stage || '',
    name: mission.name || '',
    purpose: mission.purpose || '',
    image_url: mission.image_url || '',
    sort_order: mission.sort_order ?? 1,
    reward_fe: mission.reward_fe ?? 100,
    next_mission_id: mission.next_mission_id ?? '',
    completion_mode: mission.completion_mode || 'all',
    required_count: mission.required_count ?? '',
    required_streak_days: mission.required_streak_days ?? '',
    is_active: mission.is_active !== false,
    requirements: (mission.requirements || []).map((r, i) => ({
      id: r.id,
      action_key: r.action_key || '',
      target_value: r.target_value ?? 1,
      metric_type: r.metric_type || 'count',
      distinct_dimension: r.distinct_dimension || '',
      pool_group: r.pool_group || 'required',
      sort_order: r.sort_order ?? i + 1,
      label: r.label || '',
    })),
  };
}

/** The metric a requirement is saved with. Game actions are locked to the metric the backend counts. */
export function effectiveMetric(requirement) {
  return lockedCareerMetric(requirement.action_key) || requirement.metric_type;
}

export function formToMissionPayload(form) {
  const payload = {
    stage: form.stage.trim(),
    name: form.name.trim(),
    purpose: form.purpose.trim(),
    image_url: form.image_url.trim(),
    sort_order: Number(form.sort_order),
    reward_fe: Number(form.reward_fe),
    next_mission_id: form.next_mission_id === '' || form.next_mission_id == null
      ? null
      : Number(form.next_mission_id),
    completion_mode: form.completion_mode,
    is_active: form.is_active,
    requirements: form.requirements.map((r, i) => {
      const req = {
        action_key: r.action_key,
        target_value: Number(r.target_value),
        metric_type: effectiveMetric(r),
        pool_group: r.pool_group,
        sort_order: Number(r.sort_order) || i + 1,
      };
      if (r.distinct_dimension) {
        req.distinct_dimension = r.distinct_dimension;
      }
      if (String(r.label || '').trim()) {
        req.label = r.label.trim();
      }
      return req;
    }),
  };

  if (form.completion_mode === 'any_n_of_m' || form.completion_mode === 'streak_plus_any_n') {
    payload.required_count = form.required_count === '' ? null : Number(form.required_count);
  }
  if (form.required_streak_days !== '') {
    payload.required_streak_days = Number(form.required_streak_days);
  }

  return payload;
}
